import * as Popover from '@radix-ui/react-popover';
import { useVirtualizer } from '@tanstack/react-virtual';
import React, {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { flushSync } from 'react-dom';
import { PlayerRefContext } from '../../../context-provider';
import {
  FEATURE_FONT_FAMILY_CONTROLS_PREVIEW_ON_HOVER,
  FEATURE_FONT_FAMILY_DROPDOWN_RENDER_IN_FONT,
} from '../../../flags';
import { changeItemsWithSelection } from '../../../state/actions/change-items';
import { editAndRelayoutText } from '../../../state/actions/edit-and-relayout-text';
import { setTextItemHoverPreview } from '../../../state/actions/set-hover-preview';
import { getCaptionItemAtFrame } from '../../../utils/get-caption-item-at-frame';
import { loadFontFromTextItem } from '../../../utils/text/load-font-from-text-item';
import { makeFontPreviewName } from '../../../utils/text/load-font-preview';
import { resolveFontFamilyCssStack } from '../../../utils/text/font-family-css-stack';
import { useFontPreviewLoader } from '../../../utils/text/use-font-preview-loader';
import { useAllItems, useWriteContext } from '../../../utils/use-context';
import { useTimelinePosition } from '../../../utils/use-timeline-position';
import { useTargetItemIds } from '../../utils/use-target-item-ids';
import { useSelectionValues } from '../../utils/use-selection-values';
import { InspectorSubLabel } from '../../components/inspector-label';
import { FontFamilySelectionItem } from './font-family-selection-item';
import { useLocale, useTranslations } from 'next-intl';
import { Search, Title } from '@crabs-ai/web-icons';
import { SelectFieldTrigger } from '@crabs-ai/ds-react';
import { TextItem } from '../../../items/text/text-item-type';
import { CaptionsItem } from '../../../items/captions/captions-item-type';
import {
  FONT_FAMILY_LIST,
  FontFamilyValue,
  getFontDefaultWeight,
} from '@/features/remotion-editor/editor/utils/font';
import { useGetSpaceUiProfileApi } from '@/shared/apis/space/get-space-ui-profile';
import { useFeatureEnabled } from '@/shared/hooks/useFeatureEnabled';
import { useTextSelection } from '../../../contexts/text-selection-context';
import { canShowCustomerFont } from './customer-font-access-policy';

// 추천 폰트 미설정 space 에서 memo 의존성이 매 렌더 새 배열이 되지 않도록 고정 참조 사용
const EMPTY_FONT_IDS: readonly string[] = [];

const FONT_ITEM_HEIGHT = 40;
const FALLBACK_FONT_FAMILY: FontFamilyValue = 'Pretendard';

type FontOption = {
  key: FontFamilyValue;
  displayName: string;
  cssFamily: string;
};

// Remotion 전용 로컬 폰트 메타를 한 번만 계산해 재사용
const FONT_OPTIONS: FontOption[] = FONT_FAMILY_LIST.map((font) => ({
  key: font.key as FontFamilyValue,
  displayName: font.name,
  cssFamily: font.value,
}));

const getFallbackFontOption = (): FontOption => {
  return (
    FONT_OPTIONS.find((font) => font.key === FALLBACK_FONT_FAMILY) ??
    FONT_OPTIONS[0]!
  );
};

// 도메인 별로 저장된 다양한 표기를 모두 CSS font-family 이름으로 정규화
const resolveFontOption = (fontFamily: string): FontOption => {
  if (!fontFamily) {
    return getFallbackFontOption();
  }

  const normalized = fontFamily.toLowerCase();
  const match =
    FONT_OPTIONS.find((font) => font.cssFamily.toLowerCase() === normalized) ??
    FONT_OPTIONS.find(
      (font) => font.displayName.toLowerCase() === normalized,
    ) ??
    FONT_OPTIONS.find((font) => font.key.toLowerCase() === normalized);

  return match ?? getFallbackFontOption();
};

// segments 내 폰트가 혼합되어 있는지 확인
const MIXED_FONT_MARKER = '__MIXED_FONT__';

const getFontFamilyFromTextItem = (item: TextItem | CaptionsItem): string => {
  if (item.type !== 'text' || !item.segments || item.segments.length === 0) {
    return item.fontFamily;
  }

  // 모든 segment의 폰트 수집 (없으면 item.fontFamily 사용)
  const fonts = item.segments.map((seg) => seg.fontFamily ?? item.fontFamily);
  const firstFont = fonts[0];
  const allSame = fonts.every((f) => f === firstFont);

  return allSame ? firstFont! : MIXED_FONT_MARKER;
};

const FontFamilyControlUnmemoized: React.FC<{
  fontFamily: string;
  itemId: string;
  disableCharacterStyleEditing?: boolean;
}> = ({ fontFamily, itemId, disableCharacterStyleEditing = false }) => {
  const t = useTranslations();
  const locale = useLocale();
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const triggerRef = useRef<HTMLDivElement>(null);
  const { loadFontForPreview, loadedFonts } = useFontPreviewLoader();
  const { setState } = useWriteContext();
  const { enable: isOperator } = useFeatureEnabled();
  // space UI 프로파일 serve 의 resources 로 폰트 게이트/추천을 결정한다(구 tenantId 상수 대체).
  // 미설정(null) space 는 base 폴백(게이트 폰트 숨김·추천 없음).
  const { data: uiProfileData } = useGetSpaceUiProfileApi();
  const resources = uiProfileData?.data?.resources ?? null;
  const allowedFontIds = resources?.allowedFontIds ?? null;
  const recommendedFontIds = resources?.recommendedFontIds ?? EMPTY_FONT_IDS;
  const searchPlaceholder = t.has('Editor.inspector.text.search-placeholder')
    ? t('Editor.inspector.text.search-placeholder')
    : '무엇을 찾을까요?';
  const targetIds = useTargetItemIds(itemId);
  const { isMixed: isMultiItemMixed, value: selectedFontFamily } =
    useSelectionValues<TextItem | CaptionsItem, string>(
      itemId,
      getFontFamilyFromTextItem,
    );

  // 선택 영역 내 Mixed 마커
  const SELECTION_MIXED = '__SELECTION_MIXED__';

  const {
    selectionStyle,
    applyFontFamily: applyFontFamilyToSelection,
    previewFontFamily: previewFontFamilyToSelection,
    cancelFontFamilyPreview,
  } = useTextSelection();

  // 최종 Mixed 여부 결정
  // 1. 편집 모드에서 텍스트 선택 시 → selectionStyle.fontFamily가 Mixed 마커인지 확인
  // 2. 그 외 → 여러 아이템 혼합 또는 segments 내 혼합
  const isMixed = selectionStyle.hasSelection
    ? selectionStyle.fontFamily === SELECTION_MIXED
    : isMultiItemMixed || selectedFontFamily === MIXED_FONT_MARKER;

  // Popover 열릴 때 선택 상태 및 원본 폰트 저장
  const hadSelectionOnOpenRef = useRef(false);
  const originalFontOnOpenRef = useRef<string | null>(null);

  const playerRef = useContext(PlayerRefContext);
  const currentFrame = useTimelinePosition({ playerRef: playerRef! });
  const { items } = useAllItems();

  // 표시할 폰트 결정
  // 1. 편집 모드에서 텍스트 선택 시 → selectionStyle.fontFamily (Mixed면 fontFamily prop 사용)
  // 2. 그 외 → fontFamily prop
  const displayFontFamily = selectionStyle.hasSelection
    ? selectionStyle.fontFamily === SELECTION_MIXED
      ? fontFamily
      : (selectionStyle.fontFamily ?? fontFamily)
    : fontFamily;

  // 상태에 저장된 raw 폰트명을 안전하게 정규화
  const resolvedFont = useMemo(
    () => resolveFontOption(displayFontFamily),
    [displayFontFamily],
  );

  const sortedFontOptions = useMemo(() => {
    const sortLocale = locale === 'ko' ? 'ko' : 'en';
    const collator = new Intl.Collator(sortLocale, { sensitivity: 'base' });
    const recommendedRank = new Map(
      recommendedFontIds.map((id, index) => [id, index] as const),
    );

    return FONT_OPTIONS.filter((font) =>
      canShowCustomerFont({
        fontFamily: font.key,
        allowedFontIds,
        isOperator,
      }),
    ).sort((a, b) => {
      // 추천 폰트를 목록 순서대로 앞에, 그 외는 로케일 알파벳 순
      const aRank = recommendedRank.get(a.key);
      const bRank = recommendedRank.get(b.key);
      if (aRank != null && bRank != null) return aRank - bRank;
      if (aRank != null) return -1;
      if (bRank != null) return 1;
      return collator.compare(t(`Fonts.${a.key}`), t(`Fonts.${b.key}`));
    });
  }, [isOperator, locale, t, allowedFontIds, recommendedFontIds]);

  // 검색어와 일치하는 로컬 폰트만 노출
  const filteredFonts = useMemo(() => {
    if (!search) {
      return sortedFontOptions;
    }

    const normalized = search.toLowerCase();

    return sortedFontOptions.filter((font) =>
      t('Fonts.' + font.key)
        .toLowerCase()
        .includes(normalized),
    );
  }, [search, sortedFontOptions, t]);

  useEffect(() => {
    setHighlightedIndex(-1);
  }, [search]);

  const listRef = useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: filteredFonts.length,
    getScrollElement: () => listRef.current,
    estimateSize: () => FONT_ITEM_HEIGHT,
    overscan: 30,
  });

  const virtualItems = virtualizer.getVirtualItems();

  const selectedFontIndex = useMemo(() => {
    return filteredFonts.findIndex(
      (font) => font.cssFamily === resolvedFont.cssFamily,
    );
  }, [filteredFonts, resolvedFont]);

  // 현재 선택된 폰트는 항상 미리 로딩
  useEffect(() => {
    loadFontForPreview(resolvedFont.cssFamily);
  }, [resolvedFont.cssFamily, loadFontForPreview]);

  // 팝오버가 열린 동안 뷰포트에 보이는 폰트만 선별적으로 로드
  useEffect(() => {
    if (!isOpen) return;

    const loadVisibleFonts = async () => {
      const fontsToLoad = virtualItems
        .map((item) => filteredFonts[item.index]?.cssFamily)
        .filter(Boolean) as string[];

      await Promise.all(
        fontsToLoad.map((cssFamily) => loadFontForPreview(cssFamily)),
      );
    };

    loadVisibleFonts();
  }, [isOpen, virtualItems, filteredFonts, loadFontForPreview]);

  useEffect(() => {
    if (!isOpen) return;

    const getScrollParent = (el: HTMLElement | null): HTMLElement | null => {
      if (!el) return null;
      let current: HTMLElement | null = el.parentElement;
      while (current) {
        const style = window.getComputedStyle(current);
        const overflowY = style.overflowY;
        if (
          (overflowY === 'auto' || overflowY === 'scroll') &&
          current.scrollHeight > current.clientHeight
        ) {
          return current;
        }
        current = current.parentElement;
      }
      return null;
    };

    const scrollParent = getScrollParent(triggerRef.current);
    if (!scrollParent) return;

    const handleScroll = () => setIsOpen(false);
    scrollParent.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      scrollParent.removeEventListener('scroll', handleScroll);
    };
  }, [isOpen]);

  // 드롭다운 트리거 자체에서도 샘플 텍스트 미리보기 적용
  const fontPreviewStyle = useMemo(() => {
    if (isMixed) {
      return {
        fontFamily: 'inherit',
      };
    }

    return {
      fontFamily: FEATURE_FONT_FAMILY_DROPDOWN_RENDER_IN_FONT
        ? resolveFontFamilyCssStack(makeFontPreviewName(resolvedFont.cssFamily))
        : 'inherit',
    };
  }, [isMixed, resolvedFont.cssFamily]);

  // 선택된 폰트를 아이템 상태에 반영하면서 hover 프리뷰 초기화
  const applyFontFamily = useCallback(
    (newFontFamily: string) => {
      // 해당 폰트의 기본 weight 조회
      const defaultWeight = String(getFontDefaultWeight(newFontFamily));

      setState({
        update: (state) => {
          const newState = changeItemsWithSelection(
            state,
            itemId,
            targetIds,
            (i) => {
              if (i.type === 'text') {
                return editAndRelayoutText(i, () => {
                  console.log('[applyFontFamily] BEFORE:', {
                    fontFamily: i.fontFamily,
                    segments: JSON.stringify(i.segments),
                  });

                  if (
                    i.fontFamily === newFontFamily &&
                    i.fontStyle.variant === 'normal' &&
                    i.fontStyle.weight === defaultWeight
                  ) {
                    console.log('[applyFontFamily] SKIP - no change needed');
                    return i;
                  }

                  // segments에서 fontFamily 속성 제거
                  let newSegments = i.segments?.map((seg) => {
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    const { fontFamily: _fontFamily, ...rest } = seg;
                    return rest;
                  });
                  // 모든 segment가 빈 객체(text만 있는)면 segments 제거
                  const hasStyledSegments = newSegments?.some(
                    (s) =>
                      s.color ||
                      s.fontWeight ||
                      s.fontVariant ||
                      s.fontSize ||
                      s.opacity !== undefined,
                  );
                  if (!hasStyledSegments) {
                    newSegments = undefined;
                  }

                  const result = {
                    ...i,
                    fontFamily: newFontFamily,
                    fontStyle: {
                      variant: 'normal',
                      weight: defaultWeight,
                    },
                    segments: newSegments,
                  };

                  console.log('[applyFontFamily] AFTER:', {
                    fontFamily: result.fontFamily,
                    segments: JSON.stringify(result.segments),
                  });

                  return result;
                });
              }
              if (i.type === 'captions') {
                if (
                  i.fontFamily === newFontFamily &&
                  i.fontStyle.variant === 'normal' &&
                  i.fontStyle.weight === defaultWeight
                ) {
                  return i;
                }

                return {
                  ...i,
                  fontFamily: newFontFamily,
                  fontStyle: {
                    variant: 'normal',
                    weight: defaultWeight,
                  },
                };
              }
              throw new Error(
                'Item type does not implement font change: ' +
                  JSON.stringify(i),
              );
            },
          );

          return setTextItemHoverPreview({
            state: newState,
            hoverPreview: null,
          });
        },
        commitToUndoStack: true,
      });
    },
    [itemId, setState, targetIds],
  );

  const onFontChange = useCallback(
    async (family: string) => {
      // await 전에 선택 상태를 먼저 저장 (async 중에 변경될 수 있음)
      const hadSelection = hadSelectionOnOpenRef.current;

      // 해당 폰트의 기본 weight로 로드
      const defaultWeight = String(getFontDefaultWeight(family));

      await loadFontFromTextItem({
        fontFamily: family,
        fontVariant: 'normal',
        fontWeight: defaultWeight,
        fontInfosDuringRendering: null,
      });

      // 텍스트 선택이 있었으면 선택 영역에만 적용
      if (hadSelection) {
        applyFontFamilyToSelection(family);
        return;
      }

      applyFontFamily(family);
    },
    [applyFontFamily, applyFontFamilyToSelection],
  );

  const closePopover = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleFontSelect = useCallback(
    (newFontFamily: string) => {
      // onFontChange에서 hadSelectionOnOpenRef.current를 확인하므로 먼저 호출
      onFontChange(newFontFamily);

      // closePopover에서 cancelFontFamilyPreview가 호출되지 않도록 false로 설정
      hadSelectionOnOpenRef.current = false;
      closePopover();
    },
    [onFontChange, closePopover],
  );

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      flushSync(() => setIsOpen(newOpen));

      if (newOpen) {
        // 열릴 때 선택 상태 및 원본 폰트 저장
        // disableCharacterStyleEditing이 true면 선택 상태를 무시
        hadSelectionOnOpenRef.current = disableCharacterStyleEditing
          ? false
          : selectionStyle.hasSelection;
        originalFontOnOpenRef.current = displayFontFamily;

        flushSync(() => {
          virtualizer.measure();
        });

        if (selectedFontIndex !== -1) {
          virtualizer.scrollToIndex(selectedFontIndex, {
            align: 'center',
            behavior: 'auto',
          });
          setHighlightedIndex(selectedFontIndex);
        } else {
          setHighlightedIndex(0);
        }
      } else {
        // 일부 텍스트 선택 상태였으면 Preview 취소 (원본 복원)
        if (hadSelectionOnOpenRef.current) {
          cancelFontFamilyPreview();
        }

        // 전체 선택 hover preview 초기화
        setState({
          update: (state) => {
            return setTextItemHoverPreview({
              state,
              hoverPreview: null,
            });
          },
          commitToUndoStack: false,
        });

        setHighlightedIndex(-1);
        originalFontOnOpenRef.current = null;
      }
    },
    [
      selectedFontIndex,
      virtualizer,
      setState,
      selectionStyle.hasSelection,
      cancelFontFamilyPreview,
      displayFontFamily,
      disableCharacterStyleEditing,
    ],
  );

  const previewedFont = useRef<string | null>(null);

  // Hover 이벤트에서 텍스트 아이템의 폰트만 임시 교체
  const previewFont = useCallback(
    async (fontFamilyToPreview: string) => {
      if (!FEATURE_FONT_FAMILY_CONTROLS_PREVIEW_ON_HOVER) {
        return;
      }

      // 일부 텍스트 선택 상태일 때는 선택 영역에만 Preview 적용
      if (hadSelectionOnOpenRef.current) {
        await loadFontFromTextItem({
          fontFamily: fontFamilyToPreview,
          fontVariant: 'normal',
          fontWeight: '400',
          fontInfosDuringRendering: null,
        });
        previewFontFamilyToSelection(fontFamilyToPreview);
        return;
      }

      // 전체 선택일 때는 textItemHoverPreview 사용
      const currentItem = items[itemId];
      let previewItemId = itemId;

      // Caption인 경우: 선택된 것 중 현재 프레임에 있는 것만 미리보기
      if (currentItem?.type === 'captions') {
        const captionAtFrame = getCaptionItemAtFrame({
          items,
          targetIds,
          currentFrame,
        });

        if (!captionAtFrame) {
          return;
        }

        previewItemId = captionAtFrame.id;
      }

      previewedFont.current = fontFamilyToPreview;
      await loadFontFromTextItem({
        fontFamily: fontFamilyToPreview,
        fontVariant: 'normal',
        fontWeight: '400',
        fontInfosDuringRendering: null,
      });

      if (previewedFont.current === fontFamilyToPreview) {
        setState({
          update: (state) => {
            return {
              ...state,
              textItemHoverPreview: {
                itemId: previewItemId,
                fontFamily: fontFamilyToPreview,
                type: 'font-family',
              },
            };
          },
          commitToUndoStack: false,
        });
      }
    },
    [
      itemId,
      setState,
      items,
      targetIds,
      currentFrame,
      previewFontFamilyToSelection,
    ],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!isOpen) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightedIndex((prev) => {
            const newIndex =
              prev === -1 ? 0 : Math.min(prev + 1, filteredFonts.length - 1);
            const nextFont = filteredFonts[newIndex];
            if (nextFont) {
              previewFont(nextFont.cssFamily);
              virtualizer.scrollToIndex(newIndex, {
                align: 'center',
                behavior: 'auto',
              });
            }
            return newIndex;
          });
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightedIndex((prev) => {
            const newIndex =
              prev === -1 ? filteredFonts.length - 1 : Math.max(prev - 1, 0);
            const nextFont = filteredFonts[newIndex];
            if (nextFont) {
              previewFont(nextFont.cssFamily);
              virtualizer.scrollToIndex(newIndex, {
                align: 'center',
                behavior: 'auto',
              });
            }
            return newIndex;
          });
          break;
        case 'Enter':
          e.preventDefault();
          if (
            highlightedIndex >= 0 &&
            highlightedIndex < filteredFonts.length
          ) {
            const selectedFont = filteredFonts[highlightedIndex];
            if (selectedFont) {
              handleFontSelect(selectedFont.cssFamily);
            }
          }
          break;
        case 'Escape':
          e.preventDefault();
          closePopover();
          break;
      }
    },
    [
      isOpen,
      filteredFonts,
      highlightedIndex,
      virtualizer,
      handleFontSelect,
      closePopover,
      previewFont,
    ],
  );

  const onPointerDownOutside = useCallback(
    (e: CustomEvent<{ originalEvent: PointerEvent }>) => {
      closePopover();
      e.stopPropagation();
    },
    [closePopover],
  );

  const resetFontFamily = useCallback(() => {
    setState({
      update: (state) => {
        return setTextItemHoverPreview({
          state,
          hoverPreview: null,
        });
      },
      commitToUndoStack: false,
    });
  }, [setState]);

  return (
    <div>
      <InspectorSubLabel>
        {t('Editor.inspector.text.font-style')}
      </InspectorSubLabel>
      <Popover.Root
        open={isOpen}
        onOpenChange={handleOpenChange}
      >
        <Popover.Trigger asChild>
          <div ref={triggerRef}>
            <SelectFieldTrigger
              value={isMixed ? undefined : t(`Fonts.${resolvedFont.key}`)}
              hasMixedValue={isMixed}
              leadingNode={<Title className="size-[20px]" />}
              valueStyle={fontPreviewStyle}
              onMouseDown={(e) => {
                if (selectionStyle.hasSelection) {
                  e.preventDefault();
                }
              }}
            />
          </div>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            onPointerDownOutside={onPointerDownOutside}
            side="left"
            sideOffset={8}
            align="start"
            collisionPadding={16}
            className="z-50 flex max-h-[600px] w-[360px] flex-col overflow-hidden rounded-xs border border-sys-style-neutral-default bg-sys-bg-surface-1 p-0 px-3xs py-2xs shadow-[0px_0px_16px_0px_rgba(4,4,5,0.08)] data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0"
            onKeyDown={handleKeyDown}
            onMouseDown={(e) => e.preventDefault()}
            onOpenAutoFocus={(e) => {
              // 글자 선택 상태일 때 autoFocus 방지 (selection 유지)
              if (hadSelectionOnOpenRef.current) {
                e.preventDefault();
              }
            }}
          >
            <div className="w-full border-b border-sys-style-neutral-default px-xs py-2xs">
              <div className="flex h-xl w-full items-center gap-3xs rounded-xs border border-sys-style-neutral-light bg-sys-style-neutral-lightest px-sm py-2xs">
                <Search className="size-[20px] shrink-0 [&_path]:fill-sys-cont-neutral-lightest" />
                <input
                  type="text"
                  placeholder={searchPlaceholder}
                  value={search}
                  aria-label={searchPlaceholder}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full appearance-none bg-transparent text-comp-moderate-medium text-sys-cont-neutral-default outline-hidden placeholder:text-sys-cont-neutral-lightest"
                  autoFocus={!hadSelectionOnOpenRef.current}
                />
              </div>
            </div>
            <div
              ref={listRef}
              className="min-h-0 flex-1 overflow-y-auto"
            >
              <div
                style={{
                  height: `${virtualizer.getTotalSize()}px`,
                  width: '100%',
                  position: 'relative',
                }}
              >
                {virtualItems.map((virtualItem) => {
                  const font = filteredFonts[virtualItem.index];

                  if (!font) {
                    throw new Error(
                      'Font not found for virtual item: ' + virtualItem.index,
                    );
                  }

                  // Preview 중에는 원본 폰트로 선택 여부 판단 (체크박스 고정)
                  const originalFont = originalFontOnOpenRef.current
                    ? resolveFontOption(originalFontOnOpenRef.current)
                    : resolvedFont;
                  const isSelected = originalFont.cssFamily === font.cssFamily;
                  const isHighlighted = highlightedIndex === virtualItem.index;
                  const isLoaded = FEATURE_FONT_FAMILY_DROPDOWN_RENDER_IN_FONT
                    ? loadedFonts.has(font.cssFamily)
                    : true;

                  return (
                    <FontFamilySelectionItem
                      fontFamily={font.cssFamily}
                      isLoaded={isLoaded}
                      start={virtualItem.start}
                      size={virtualItem.size}
                      isHighlighted={isHighlighted}
                      isSelected={isSelected}
                      applyFontFamily={handleFontSelect}
                      setIsOpen={setIsOpen}
                      previewedFont={previewedFont}
                      key={virtualItem.key}
                      resetFontFamily={resetFontFamily}
                      previewFont={previewFont}
                    />
                  );
                })}
              </div>
            </div>
            {filteredFonts.length === 0 && (
              <div className="px-sm py-xs text-body-small text-sys-cont-neutral-light">
                {t('Editor.inspector.text.no-results')}
              </div>
            )}
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </div>
  );
};

export const FontFamilyControl = memo(FontFamilyControlUnmemoized);
